import React, { useEffect, useState } from 'react';

// Always-available "install this app on your phone" button for the dashboard and
// program pages. Unlike a one-off banner it never disappears, so a user can install
// whenever they're ready. People already running the installed app get a quiet
// "installed" confirmation instead of a prominent CTA.
//  - Android/Chrome: uses the captured `beforeinstallprompt` for a one-tap native install.
//  - iOS Safari / browsers with no prompt event: tapping reveals the manual steps.

const C = { dark: '#1F4E5A', bg: '#F2F6F7', warm: '#E3EEF0', brown: '#5A7480', soft: '#9FCAD2' };

function isStandalone() {
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;
}
function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

const PhoneIcon = ({ color }) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="6" y="2" width="12" height="20" rx="3" /><path d="M11 5h2" /><path d="M12 18h.01" />
  </svg>
);

export default function InstallAppButton() {
  const [deferred, setDeferred] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const ios = isIos();

  useEffect(() => {
    setStandalone(isStandalone());
    // The prompt may have been captured by the early index.html handler before this
    // mounted — pick it up, and also listen for fresh ones.
    if (window.__btbInstallPrompt) setDeferred(window.__btbInstallPrompt);
    const onPrompt = (e) => { e.preventDefault && e.preventDefault(); window.__btbInstallPrompt = e; setDeferred(e); };
    const onInstallable = () => setDeferred(window.__btbInstallPrompt || null);
    const onInstalled = () => { setStandalone(true); setDeferred(null); setShowHelp(false); window.__btbInstallPrompt = null; };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('btb-installable', onInstallable);
    window.addEventListener('appinstalled', onInstalled);
    let mq;
    try {
      mq = window.matchMedia('(display-mode: standalone)');
      mq.addEventListener && mq.addEventListener('change', () => setStandalone(isStandalone()));
    } catch {}
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('btb-installable', onInstallable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleClick = async () => {
    const promptEvt = deferred || window.__btbInstallPrompt;
    if (promptEvt) {
      promptEvt.prompt();
      try { await promptEvt.userChoice; } catch {}
      setDeferred(null);
      window.__btbInstallPrompt = null;
      return;
    }
    // No native prompt (iOS, already installed in this browser, or not yet eligible) —
    // reveal the manual steps so it's still actionable.
    setShowHelp(s => !s);
  };

  // Already using the installed app → keep it understated, just a quiet confirmation.
  if (standalone) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, color: C.brown, fontSize: 12.5, padding: '4px 0' }}>
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke={C.brown} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        Umeweka app kwenye simu yako · App installed
      </div>
    );
  }

  return (
    <div>
      <button onClick={handleClick}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, background: '#fff', color: C.dark, border: `1.5px solid ${C.soft}`, borderRadius: 12, padding: '12px 18px', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
        <PhoneIcon color={C.dark} />
        Weka app hii kwenye simu yako
        <span style={{ fontWeight: 400, fontSize: 11.5, color: C.brown }}>Install this app</span>
      </button>
      {showHelp && (
        <div style={{ marginTop: 8, background: C.warm, borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: C.dark, lineHeight: 1.6 }}>
          {ios
            ? <>Bonyeza <strong>Share</strong> kisha <strong>“Add to Home Screen”</strong>.<br /><span style={{ color: C.brown }}>Tap Share, then “Add to Home Screen”.</span></>
            : <>Fungua menyu ya kivinjari (⋮) kisha chagua <strong>“Install app”</strong> / <strong>“Add to Home screen”</strong>.<br /><span style={{ color: C.brown }}>Open the browser menu (⋮), then “Install app”.</span></>}
        </div>
      )}
    </div>
  );
}
